import { type ReactNode, useRef } from 'react';

import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { cn } from '@/lib/utils';

export interface MarqueeProps {
  children: ReactNode;
  className?: string;
  /** Pixels per second. Default 60 — a calm, institutional drift. */
  speed?: number;
  /** Scroll direction. */
  direction?: 'left' | 'right';
  /** Slow (not stop) while the pointer is over the track. */
  pauseOnHover?: boolean;
  /** Tailwind gap utility for spacing between items. */
  gap?: string;
}

/**
 * Seamless, infinitely looping marquee for accreditation marks, partner logos
 * and rolling proof points. Two identical groups sit side by side; the track is
 * translated by exactly one group width + one gap, so the wrap is invisible.
 * Under reduced motion the row is static and horizontally scrollable — never
 * auto-scrolling (satisfies the "no disruptive autoplay" brand rule).
 */
export function Marquee({
  children,
  className,
  speed = 60,
  direction = 'left',
  pauseOnHover = true,
  gap = 'gap-12',
}: MarqueeProps) {
  const ref = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const track = trackRef.current;
      const group = groupRef.current;
      if (reduced || !track || !group) return;

      const gapPx = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      const distance = group.offsetWidth + gapPx;
      const duration = distance / speed;
      const from = direction === 'left' ? 0 : -distance;
      const to = direction === 'left' ? -distance : 0;

      const tween = gsap.fromTo(track, { x: from }, { x: to, duration, ease: 'none', repeat: -1 });

      if (pauseOnHover) {
        const enter = () => gsap.to(tween, { timeScale: 0.15, duration: 0.4 });
        const leave = () => gsap.to(tween, { timeScale: 1, duration: 0.4 });
        track.addEventListener('pointerenter', enter);
        track.addEventListener('pointerleave', leave);
        return () => {
          track.removeEventListener('pointerenter', enter);
          track.removeEventListener('pointerleave', leave);
        };
      }
    },
    { scope: ref, dependencies: [reduced, speed, direction, pauseOnHover] },
  );

  return (
    <div
      ref={ref}
      className={cn(
        'relative w-full overflow-hidden',
        '[mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]',
        reduced && 'overflow-x-auto',
        className,
      )}
    >
      <div ref={trackRef} className={cn('flex w-max flex-nowrap items-center', gap)}>
        <div ref={groupRef} className={cn('flex flex-nowrap items-center', gap)}>
          {children}
        </div>
        {!reduced && (
          <div aria-hidden="true" className={cn('flex flex-nowrap items-center', gap)}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}
